"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import StatsSection from "./components/StatsSection";
import WhatIs from "./components/WhatIs";
import WhenYouNeed from "./components/WhenYouNeed";
import ProcessSection from "./components/ProcessSection";
import Included from "./components/Included";
import NeighborhoodCoverage from "./components/NeighborhoodCoverage";
import ScopeSection from "./components/ScopeSection";
import FaqSection from "@/components/feature/FaqSection";
import ServiceAreasGrid from "@/components/feature/ServiceAreasGrid";
import RelatedServicesBlockService from "@/components/feature/RelatedServicesBlockService";
import BottomCta from "./components/BottomCta";
import LeadFormSection from "@/components/feature/LeadFormSection";
import AboutServiceSection from "@/components/feature/AboutServiceSection";
import { injectMetaTags } from "@/lib/seoHelpers";

const boardupFaqs = [
  { qKey: "boardup.faq1Q", aKey: "boardup.faq1A" },
  { qKey: "boardup.faq2Q", aKey: "boardup.faq2A" },
  { qKey: "boardup.faq3Q", aKey: "boardup.faq3A" },
];

export default function EmergencyBoardUpPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const boardupAboutImg =
    "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/0b29484b-f503-450c-8ad4-9bee4a953548_Board-Up-Service-5.jpg?v=abcc4f8ca8b740d94261682d8e4a29e4";

  useEffect(() => {
    document.title = t("boardup.seoTitle");

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", t("boardup.seoDesc"));

    const cleanupSeo = injectMetaTags({
      title: t("boardup.seoTitle"),
      description: t("boardup.seoDesc"),
      url: "https://impact-window-repair-miami.com/emergency-board-up/",
      image: "https://impact-window-repair-miami.com/og/og-boardup.jpg",
      imageAlt: t("boardup.ogImageAlt"),
      breadcrumbs: [
        { name: t("nav.home"), item: "https://impact-window-repair-miami.com/" },
        { name: t("boardup.breadcrumbName"), item: "https://impact-window-repair-miami.com/emergency-board-up/" }
      ],
      type: "website",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: t("boardup.breadcrumbName"),
      provider: {
        "@type": "LocalBusiness",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        email: "contact@impact-window-repair-miami.com",
        url: "https://impact-window-repair-miami.com",
        inLanguage: currentLang === "es" ? "es-US" : "en-US",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 25.7617,
          longitude: -80.1918
        },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          opens: "07:00",
          closes: "21:00"
        }],
        priceRange: "$$",
        paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
        currenciesAccepted: "USD",
        areaServed: [
          {"@type": "City", "name": "Miami"},
          {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
          "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
        ],
        knowsLanguage: ["en", "es"],
        knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
        slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee."
      },
      serviceType: "Emergency Window Board-Up",
      description: t("boardup.seoDesc"),
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: boardupFaqs.map((faq) => ({
        "@type": "Question",
        name: t(faq.qKey),
        acceptedAnswer: {
          "@type": "Answer",
          text: t(faq.aKey),
        },
      })),
    };

    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const faqScript = document.createElement("script");
    faqScript.setAttribute("type", "application/ld+json");
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, [currentLang]);

  return (
    <div className="font-roboto">
      <HeaderStrip />
      <HeroSection />
      <LeadFormSection defaultService="boardup" />
      <AboutServiceSection
        badge={t("boardup.aboutBadge")}
        imageSrc={boardupAboutImg}
        imageAlt={t("boardup.ogImageAlt")}
        heading={t("boardup.aboutHeading")}
        lead={t("boardup.aboutLead")}
        bullets={[
          t("boardup.aboutBullet1"),
          t("boardup.aboutBullet2"),
          t("boardup.aboutBullet3"),
          t("boardup.aboutBullet4"),
          t("boardup.aboutBullet5"),
        ]}
        closing={t("boardup.aboutClosing")}
      />
      <StatsSection />
      <WhatIs />
      <WhenYouNeed />
      <ProcessSection />
      <Included />
      <NeighborhoodCoverage />
      <ScopeSection />
      <FaqSection
        items={boardupFaqs.map((f) => ({ question: t(f.qKey), answer: t(f.aKey) }))}
      />
      <ServiceAreasGrid
        serviceName={t("boardup.breadcrumbName")}
        serviceSlug="emergency-board-up"
      />
      <RelatedServicesBlockService
        links={[
          { label: t("boardup.related1"), path: "/sliding-door-repair/" },
          { label: t("boardup.related2"), path: "/window-hardware-repair/" },
          { label: t("boardup.related3"), path: "/glass-replacement/" },
        ]}
      />
      <BottomCta />
    </div>
  );
}